"use client"

import { useEffect, useRef } from "react"
import { useFormContext } from "react-hook-form"
import { animateChildren } from "@/lib/animation-utils"
import { CheckCircle, XCircle, FileText } from "lucide-react"

import { FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { Textarea } from "@/components/ui/textarea"
import { Label } from "@/components/ui/label"

export default function PregnancyResultsStep() {
  const { control, formState: { dirtyFields } } = useFormContext()
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (containerRef.current) {
      // Set initial state for children
      Array.from(containerRef.current.children).forEach((child) => {
        ;(child as HTMLElement).style.opacity = "0"
        ;(child as HTMLElement).style.transform = "translateY(20px)"
      })

      // Animate children with staggered delay
      requestAnimationFrame(() => {
        if (containerRef.current) {
          animateChildren(
            containerRef.current,
            {
              opacity: 1,
              translateY: 0,
            },
            {
              duration: 400,
              easing: "ease-out",
            },
          )
        }
      })
    }
  }, [])

  return (
    <div ref={containerRef} className="space-y-6">
      <div className="space-y-2">
        <h2 className="text-2xl font-bold text-slate-900">Pregnancy Results</h2>
        <p className="text-slate-500">Enter the final pregnancy check results</p>
      </div>

      <div className="space-y-6">
        <FormField
          control={control}
          name="finalPregnancyCheck"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-base flex items-center gap-2 flex-wrap">
                Final Pregnancy Check
                {dirtyFields.finalPregnancyCheck && (
                  <span className="text-[10px] font-medium text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded border border-amber-200 ml-2">
                    Changed
                  </span>
                )}
              </FormLabel>
              <FormControl>
                <RadioGroup onValueChange={field.onChange} value={field.value} className="flex gap-6">
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="Positive" id="positive" />
                    <Label htmlFor="positive" className="flex items-center gap-2 font-normal">
                      <CheckCircle className="h-5 w-5 text-emerald-500" />
                      Positive
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="Negative" id="negative" />
                    <Label htmlFor="negative" className="flex items-center gap-2 font-normal">
                      <XCircle className="h-5 w-5 text-red-500" />
                      Negative
                    </Label>
                  </div>
                </RadioGroup>
              </FormControl>
              {dirtyFields.finalPregnancyCheck && (
                <p className="text-[11px] text-amber-600 font-medium mt-1">
                  Data has been changed
                </p>
              )}
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="observationFinalPregnancyCheck"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="flex items-center gap-2 flex-wrap">
                <FileText className="h-4 w-4" />
                Observations
                {dirtyFields.observationFinalPregnancyCheck && (
                  <span className="text-[10px] font-medium text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded border border-amber-200 ml-2">
                    Changed
                  </span>
                )}
              </FormLabel>
              <FormControl>
                <Textarea
                  placeholder="Enter any observations or notes about the pregnancy check"
                  className="min-h-[120px] resize-none"
                  {...field}
                />
              </FormControl>
              {dirtyFields.observationFinalPregnancyCheck && (
                <p className="text-[11px] text-amber-600 font-medium mt-1">
                  Data has been changed
                </p>
              )}
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    </div>
  )
}
